import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, 
  Tag, 
  Info, 
  X, 
  ChevronRight,
  Megaphone
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { Announcement, AnnouncementType } from '../../types';

interface AnnouncementBannerProps {
  showUrgentModal?: boolean;
}

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({ showUrgentModal = true }) => {
  const { businessId } = useBusiness();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);
  const [urgentModal, setUrgentModal] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);

  // Cargar IDs descartados del localStorage
  useEffect(() => {
    const stored = localStorage.getItem(`dismissed_announcements_${businessId}`);
    if (stored) {
      try {
        setDismissedIds(JSON.parse(stored));
      } catch {
        setDismissedIds([]);
      }
    }
  }, [businessId]);

  // Cargar anuncios
  useEffect(() => {
    const loadAnnouncements = async () => {
      if (!businessId) return;

      try {
        const q = query(
          collection(db, 'announcements'),
          where('businessId', '==', businessId),
          where('isActive', '==', true),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(q);
        const now = new Date();
        
        const data = snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate() || new Date(),
            updatedAt: doc.data().updatedAt?.toDate() || new Date()
          }))
          .filter((ann: any) => {
            // Filtrar expirados
            if (ann.expirationDate && new Date(ann.expirationDate) < now) {
              return false;
            }
            // Filtrar por audiencia
            if (ann.targetAudience === 'members_only') {
              const hasMembership = currentUser?.activeMembership?.isActive || 
                                   (currentUser?.activePass && currentUser.activePass.creditsRemaining > 0);
              if (!hasMembership) return false;
            }
            return true;
          }) as Announcement[];

        setAnnouncements(data);

        // Mostrar modal para anuncios urgentes no descartados
        if (showUrgentModal) {
          const urgentNotDismissed = data.find(
            a => a.type === 'urgent' && !dismissedIds.includes(a.id)
          );
          if (urgentNotDismissed) {
            setUrgentModal(urgentNotDismissed);
          }
        }
      } catch (error) {
        console.error('Error cargando anuncios:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnnouncements();
  }, [businessId, currentUser, dismissedIds, showUrgentModal]);

  // Descartar anuncio
  const dismissAnnouncement = (id: string) => {
    const newDismissed = [...dismissedIds, id];
    setDismissedIds(newDismissed);
    localStorage.setItem(`dismissed_announcements_${businessId}`, JSON.stringify(newDismissed));
    
    if (urgentModal?.id === id) {
      setUrgentModal(null);
    }
  };

  // Click en CTA
  const handleCTA = (announcement: Announcement) => {
    if (announcement.ctaLink) {
      navigate(announcement.ctaLink);
    }
    if (urgentModal?.id === announcement.id) {
      setUrgentModal(null);
    }
  };

  // Config por tipo
  const getTypeConfig = (type: AnnouncementType) => {
    switch (type) {
      case 'urgent':
        return { 
          icon: <AlertTriangle className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-r from-red-600/20 to-rose-600/20 border-red-500/50',
          iconBg: 'bg-red-500/20',
          iconColor: 'text-red-400',
          buttonBg: 'bg-red-500 hover:bg-red-600',
          label: 'Urgente'
        };
      case 'promo':
        return { 
          icon: <Tag className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-r from-amber-600/20 to-orange-600/20 border-amber-500/50',
          iconBg: 'bg-amber-500/20',
          iconColor: 'text-amber-400',
          buttonBg: 'bg-amber-500 hover:bg-amber-600',
          label: 'Promoción'
        };
      case 'info':
      default:
        return { 
          icon: <Info className="w-5 h-5" />, 
          bgClass: 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-blue-500/50',
          iconBg: 'bg-blue-500/20',
          iconColor: 'text-blue-400',
          buttonBg: 'bg-blue-500 hover:bg-blue-600',
          label: 'Información'
        };
    }
  };

  // Filtrar anuncios visibles (no descartados, no urgentes si ya se mostró el modal)
  const visibleAnnouncements = announcements.filter(
    a => !dismissedIds.includes(a.id) && (a.type !== 'urgent' || !showUrgentModal)
  );

  if (loading) return null;

  return (
    <>
      {/* Modal de anuncio urgente */}
      {urgentModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-lg w-full overflow-hidden border-2 border-red-500/50 animate-pulse-slow">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-600 to-rose-600 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <span className="text-xs font-medium text-white/80 uppercase tracking-wider">Aviso Urgente</span>
                    <h3 className="text-xl font-bold text-white">{urgentModal.title}</h3>
                  </div>
                </div>
                <button
                  onClick={() => dismissAnnouncement(urgentModal.id)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Image */}
            {urgentModal.imageUrl && (
              <div className="h-48 overflow-hidden">
                <img 
                  src={urgentModal.imageUrl} 
                  alt="" 
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              <p className="text-gray-300 whitespace-pre-wrap">{urgentModal.body}</p>
              
              <div className="flex gap-3 mt-6">
                {urgentModal.ctaText && urgentModal.ctaLink && (
                  <button
                    onClick={() => handleCTA(urgentModal)}
                    className="flex-1 py-3 px-4 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                  >
                    {urgentModal.ctaText}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => dismissAnnouncement(urgentModal.id)}
                  className="flex-1 py-3 px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-colors"
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tarjetas de anuncios */}
      {visibleAnnouncements.length > 0 && (
        <div className="space-y-3 mb-6">
          {visibleAnnouncements.slice(0, 3).map((announcement) => {
            const config = getTypeConfig(announcement.type);
            
            return (
              <div 
                key={announcement.id}
                className={`relative rounded-xl border overflow-hidden ${config.bgClass}`}
              >
                <div className="flex">
                  {/* Image */}
                  {announcement.imageUrl && (
                    <div className="w-24 sm:w-32 flex-shrink-0">
                      <img 
                        src={announcement.imageUrl} 
                        alt="" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Content */}
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`p-1.5 rounded-lg ${config.iconBg}`}>
                          <span className={config.iconColor}>{config.icon}</span>
                        </div>
                        <span className={`text-xs font-medium ${config.iconColor} uppercase`}>
                          {config.label}
                        </span>
                      </div>
                      <button
                        onClick={() => dismissAnnouncement(announcement.id)}
                        className="p-1 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    
                    <h4 className="font-bold text-white text-sm sm:text-base">{announcement.title}</h4>
                    <p className="text-gray-400 text-xs sm:text-sm mt-1 line-clamp-2">{announcement.body}</p>
                    
                    {announcement.ctaText && announcement.ctaLink && (
                      <button
                        onClick={() => handleCTA(announcement)}
                        className={`mt-3 px-4 py-2 ${config.buttonBg} text-white text-xs sm:text-sm font-medium rounded-lg transition-colors flex items-center gap-1`}
                      >
                        {announcement.ctaText}
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* Ver más */}
          {visibleAnnouncements.length > 3 && (
            <button
              onClick={() => navigate('/announcements')}
              className="w-full py-3 text-center text-amber-400 hover:text-amber-300 text-sm font-medium transition-colors"
            >
              Ver todos los anuncios ({visibleAnnouncements.length})
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default AnnouncementBanner;




















